/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { SerializedData } from 'bernie-core';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { UitkCheckbox } from 'uitk-react-checkbox';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading } from 'uitk-react-text';
import { ActivityDestinationViewSearch } from '../../../common/__generated__/api';
import { useStores } from '../../../utils/hooks';
import { checkboxClickHandler, PRICE_CATEGORY, seeAllCheckboxClickHandler, SEE_ALL_CATEGORY } from './filter-helper';
import { rangeCheckboxClickHandler } from './range-filter-helper';

import Fields = ActivityDestinationViewSearch.ShoppingMultiSelectionFieldInlineFragment;
import Options = ActivityDestinationViewSearch.__Options;
import NestedOptions = ActivityDestinationViewSearch.NestedOptions;
import ClientSideAnalytics = ActivityDestinationViewSearch.ClientSideAnalytics;

export interface CheckboxProps {
  inDialog?: boolean;
  field: Fields;
}

export const getCheckboxKeyAndId = (key: string): SerializedData => {
  const [, filterKey, filterCategoryId] = key.split('-');

  return { filterKey, filterCategoryId };
};

export const Checkbox: React.FC<CheckboxProps> = observer(({ inDialog, field }) => {
  const { analytics, activitySearchStore } = useStores();
  const history = useHistory();
  const location = useLocation();

  const categoryName = field.primary || undefined;
  const filterOptions = activitySearchStore.sortAndFilterObject.filters;

  function updateCheckbox(event: React.ChangeEvent<HTMLInputElement>, clientSideAnalytics?: ClientSideAnalytics): void {
    const filterKey = event.target.name;
    const { filterCategoryId } = getCheckboxKeyAndId(event.target.id);
    analytics.trackEvent(clientSideAnalytics?.referrerId, clientSideAnalytics?.linkName);
    if (filterCategoryId === SEE_ALL_CATEGORY) {
      seeAllCheckboxClickHandler(filterKey, filterCategoryId, activitySearchStore, history, location, event, inDialog);
    } else if (filterCategoryId === PRICE_CATEGORY) {
      rangeCheckboxClickHandler(activitySearchStore, event, history, location, inDialog);
    } else {
      checkboxClickHandler(filterKey, filterCategoryId, activitySearchStore, history, location, event, inDialog);
    }
  }

  const renderSubCategories = (
    checked: boolean,
    categoryId: string,
    subCategoryTitle: string,
    subFilterProps?: NestedOptions[] | null
  ): JSX.Element | null => {
    if (!checked || !subFilterProps) {
      return null;
    }

    return (
      <UitkSpacing padding={{ inlinestart: 'four' }} margin={{ inlinestart: 'four' }}>
        <fieldset data-testid="filter--subcategory-options">
          <legend className="is-visually-hidden">{subCategoryTitle}</legend>
          {subFilterProps?.map((subProps) => (
            <div key={`child-category-${subProps.value}`} data-testid="filter--subcategory-options-checkbox">
              <UitkCheckbox
                id={`cb-${subProps.value}-${categoryId}`}
                name={subProps.value}
                description={subProps.description || ''}
                value={filterOptions[categoryId][subProps.value]?.selected || ''}
                checked={filterOptions[categoryId][subProps.value]?.selected || false}
                disabled={!filterOptions[categoryId][subProps.value]?.enabled || false}
                onChange={(event) =>
                  updateCheckbox(event, subProps.selected ? subProps.deselectAnalytics : subProps.selectAnalytics)
                }
              >
                {subProps.primary}
              </UitkCheckbox>
            </div>
          ))}
        </fieldset>
      </UitkSpacing>
    );
  };

  return (
    <>
      <fieldset key={categoryName}>
        {categoryName ? (
          <UitkSpacing padding={{ blockstart: 'four', blockend: 'two' }}>
            <legend data-testid="filter--category-name">
              <UitkHeading tag="h5" size={6}>
                {categoryName}
              </UitkHeading>
            </legend>
          </UitkSpacing>
        ) : null}
        {(field as Fields)?.options?.map((option: Options) => (
          <div key={`parent-category-${option.value}`} data-testid={`filter--${option.id}-options`}>
            <UitkCheckbox
              id={`cb-${option.value.replace(/ /g, '')}-${option.id}`}
              name={option.value}
              description={option.description || ''}
              value={filterOptions[option.id][option.value]?.selected || ''}
              checked={filterOptions[option.id][option.value]?.selected || false}
              disabled={!filterOptions[option.id][option.value]?.enabled || false}
              onChange={(event) =>
                updateCheckbox(event, option.selected ? option.deselectAnalytics : option.selectAnalytics)
              }
              data-testid={`filter--${option.id}-options-checkbox`}
            >
              {option.accessibility && <span className="is-visually-hidden">{option.accessibility}</span>}
              <span aria-hidden={Boolean(option.accessibility)}>{option.primary}</span>
            </UitkCheckbox>
            {renderSubCategories(
              filterOptions[option.id][option.value]?.selected,
              option.id,
              option.primary,
              option.nestedOptions
            )}
          </div>
        ))}
      </fieldset>
    </>
  );
});
